import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import './App.css';

import Login              from './components/Login';
import Test               from './components/Test';
import SetupPage          from './components/CarSetup';
import Settings           from './components/Settings';
import Testings           from './components/Testings';
import DriverAndTDHistory from './components/DriverAndTDHistory';
import SeasonPlanner      from './components/SeasonPlanner';
import DriverHistory      from './components/DriverHistory';
import TDHistory          from './components/TDHistory';
import PitHistory         from './components/PitHistory';
import WeatherHistory     from './components/WeatherHistory';
import Sponsors           from './components/Sponsors';
import RaceAnalysis       from './components/RaceAnalysis';
import Admin              from './components/Admin';
import SeasonGains        from './components/SeasonGains';
import Market             from './components/Market';
import AnalysisResults    from './components/AnalysisResults';
import VpnDevices         from './components/VpnDevices';
import GroupSpending      from './components/GroupSpending';
import Layout             from './components/Layout';
import { decodeJwtRole, api } from './components/api/api';

export const AuthContext = createContext(null);

const PrivateRoute = ({ children }) => {
    const { isLoading, tokens, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    return <Layout onLogout={logout}>{children}</Layout>;
};

const AdminOrUpdaterRoute = ({ children }) => {
    const { isLoading, tokens, userRole, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    if (userRole !== 'ADMIN' && userRole !== 'UPDATER') return <Navigate to="/setup" replace />;
    return <Layout onLogout={logout}>{children}</Layout>;
};

const AdminOrForceOneRoute = ({ children }) => {
    const { isLoading, tokens, userRole, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    if (userRole !== 'ADMIN' && userRole !== 'FORCE_ONE') return <Navigate to="/setup" replace />;
    return <Layout onLogout={logout}>{children}</Layout>;
};

const AdminOnlyRoute = ({ children }) => {
    const { isLoading, tokens, userRole, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    if (userRole !== 'ADMIN') return <Navigate to="/setup" replace />;
    return <Layout onLogout={logout}>{children}</Layout>;
};

// Season Planner — open to these manager roles (per user request 2026-11-01).
const PLANNER_ROLES = ['ADMIN', 'FORCE_ONE', 'BRUTE_FORCE', 'MIDLAND'];
const PlannerRoute = ({ children }) => {
    const { isLoading, tokens, userRole, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    if (!PLANNER_ROLES.includes(userRole)) return <Navigate to="/setup" replace />;
    return <Layout onLogout={logout}>{children}</Layout>;
};

// Section visibility matrix (admin-controlled). ADMIN always allowed; while the
// visible-set is still loading (null) we allow, to avoid flicker/lockout.
const SectionRoute = ({ path, children }) => {
    const { isLoading, tokens, userRole, visibleSections, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    if (userRole !== 'ADMIN' && visibleSections != null && !visibleSections.includes(path)) {
        return <Navigate to="/setup" replace />;
    }
    return <Layout onLogout={logout}>{children}</Layout>;
};

const SETUP_EXTRAS_ROLES = ['ADMIN', 'UPDATER', 'FORCE_ONE', 'BRUTE_FORCE'];

// Season Gains and other manager-facing "extras" (Similar races, Recommend
// upgrade, Lap/Race Gain on CarSetup) — restricted to these 4 roles.
const SetupExtrasRoute = ({ children }) => {
    const { isLoading, tokens, userRole, logout } = useContext(AuthContext);
    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117', color: '#6b7280', fontSize: 14 }}>
                Loading…
            </div>
        );
    }
    if (!tokens?.access_token) return <Navigate to="/" replace />;
    if (!SETUP_EXTRAS_ROLES.includes(userRole)) return <Navigate to="/setup" replace />;
    return <Layout onLogout={logout}>{children}</Layout>;
};

function App() {
    const [tokens, setTokensState] = useState(null);
    const [userRole, setUserRole] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [apiRequests, setApiRequestsState] = useState(() => {
        const v = localStorage.getItem('apiRequestsRemaining');
        return v !== null ? parseInt(v, 10) : null;
    });

    const [visibleSections, setVisibleSections] = useState(null);

    const setApiRequests = useCallback((val) => {
        if (val === undefined || val === null) return;
        localStorage.setItem('apiRequestsRemaining', String(val));
        setApiRequestsState(val);
    }, []);

    useEffect(() => {
        if (!tokens?.access_token) { setVisibleSections(null); return; }
        let cancelled = false;
        api.get('/section-access/mine')
            .then(r => { if (!cancelled) setVisibleSections(Array.isArray(r.data) ? r.data : []); })
            .catch(() => { if (!cancelled) setVisibleSections(null); });
        return () => { cancelled = true; };
    }, [tokens]);

    useEffect(() => {
        const access  = localStorage.getItem('accessToken');
        const refresh = localStorage.getItem('refreshToken');
        if (access && refresh) {
            setTokensState({ access_token: access, refresh_token: refresh });
            setUserRole(decodeJwtRole(access));
        }
        setIsLoading(false);
    }, []);

    const setTokens = (data) => {
        if (!data) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            setTokensState(null);
            setUserRole(null);
            return;
        }
        localStorage.setItem('accessToken', data.access_token);
        localStorage.setItem('refreshToken', data.refresh_token);
        setTokensState(data);
        setUserRole(decodeJwtRole(data.access_token));
    };

    const logout = () => setTokens(null);

    return (
        <AuthContext.Provider value={{ tokens, setTokens, logout, isLoading, apiRequests, setApiRequests, userRole, visibleSections }}>
            <BrowserRouter>
                <Routes>
                    <Route
                        path="/"
                        element={
                            isLoading ? (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0d1117' }} />
                            ) : tokens ? (
                                <Navigate to="/setup" replace />
                            ) : (
                                <Login setTokens={setTokens} />
                            )
                        }
                    />

                    <Route path="/test"           element={<PrivateRoute><Test /></PrivateRoute>} />
                    <Route path="/setup"          element={<SectionRoute path="/setup"><SetupPage /></SectionRoute>} />
                    <Route path="/settings"       element={<SectionRoute path="/settings"><Settings /></SectionRoute>} />
                    <Route path="/testings"       element={<SectionRoute path="/testings"><Testings /></SectionRoute>} />
                    <Route path="/driverhistory"  element={<SectionRoute path="/driverhistory"><DriverAndTDHistory /></SectionRoute>} />
                    <Route path="/driver-history" element={<SectionRoute path="/driver-history"><DriverHistory /></SectionRoute>} />
                    <Route path="/td-history"     element={<SectionRoute path="/td-history"><TDHistory /></SectionRoute>} />
                    <Route path="/pit-history"    element={<SectionRoute path="/pit-history"><PitHistory /></SectionRoute>} />
                    <Route path="/weather"        element={<SectionRoute path="/weather"><WeatherHistory /></SectionRoute>} />
                    <Route path="/sponsors"       element={<SectionRoute path="/sponsors"><Sponsors /></SectionRoute>} />
                    <Route path="/race-analysis"  element={<SectionRoute path="/race-analysis"><RaceAnalysis /></SectionRoute>} />
                    <Route path="/season-gains"   element={<SectionRoute path="/season-gains"><SeasonGains /></SectionRoute>} />
                    <Route path="/market"         element={<SectionRoute path="/market"><Market /></SectionRoute>} />
                    <Route path="/season-planner" element={<SectionRoute path="/season-planner"><SeasonPlanner /></SectionRoute>} />
                    <Route path="/analysis-results" element={<SectionRoute path="/analysis-results"><AnalysisResults /></SectionRoute>} />
                    <Route path="/vpn"            element={<SectionRoute path="/vpn"><VpnDevices /></SectionRoute>} />
                    <Route path="/group-spending" element={<SectionRoute path="/group-spending"><GroupSpending /></SectionRoute>} />
                    <Route path="/admin"          element={<SectionRoute path="/admin"><Admin /></SectionRoute>} />

                    <Route path="*" element={<Navigate to="/" />} />
                </Routes>
            </BrowserRouter>
        </AuthContext.Provider>
    );
}

export default App;
